import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Trash2 } from "lucide-react";

import { api } from "@/api/client";
import type { Pool, Replica, Service } from "@/api/types";
import { useAlert } from "@/components/ConfirmDialog";
import { Modal } from "@/components/Modal";
import { Pill } from "@/components/Pill";

interface Props {
  open: boolean;
  onClose: () => void;
  service: Service;
  /** Resolved display label (label override or fallback to slug). */
  displayName: string;
  /** Whether the service is still in draft (never rolled out). */
  isDraft: boolean;
  /** Pools attached to this service — for impact summary and rollout cleanup. */
  pools: Pool[];
  /** Active replicas under this service — for impact summary. */
  replicas: Replica[];
  /** Called once the controller has acked the delete. */
  onDeleted: () => void;
}

/**
 * Final-confirmation modal for tearing down a service. Two flows in
 * one component:
 *
 *   - draft     → light confirmation, single click. Nothing has ever
 *                 been provisioned, so we just need to make sure the
 *                 operator didn't fat-finger the trash icon.
 *   - active    → "type the slug" gate, plus an impact summary so the
 *                 operator sees how many pools and replicas they're
 *                 about to tear down. Matches the pattern used by
 *                 GitHub / Vercel / Linear for permanent destructive
 *                 actions.
 *
 * The controller hard-deletes the service row + every dependent
 * (workload, draft, pools, replicas, rollouts) via FK cascade. The
 * modal just triggers React Query cache invalidations so the tree
 * doesn't render ghosts after the 204.
 */
export function DeleteServiceModal({
  open,
  onClose,
  service,
  displayName,
  isDraft,
  pools,
  replicas,
  onDeleted,
}: Props) {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const [typed, setTyped] = useState("");

  // Reset the typed input every time the modal re-opens so it can't
  // be left primed across sessions.
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const liveReplicaCount = useMemo(
    () =>
      replicas.filter(
        (r) => r.status === "ready" || r.status === "provisioning",
      ).length,
    [replicas],
  );

  const remove = useMutation({
    mutationFn: async () => {
      // Active services must transition through `lifecycle_state=deleting`
      // first so the reconciler can drain SkyServe before the row is
      // hard-deleted. Drafts (never reconciled) are deleted directly.
      if (!isDraft) {
        await api.patch(`/services/${service.id}/lifecycle`, {
          lifecycle_state: "deleting",
        });
      }
      await api.delete<void>(`/services/${service.id}`);
    },
    onSuccess: () => {
      // The controller hard-deletes the service row (and via FK
      // cascade, every workload / pool / rollout / replica row that
      // hung off it). The WebUI just needs to bust its caches so the
      // tree, replica list, and rollouts panes don't show ghosts for a
      // beat.
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["rollouts"] });
      queryClient.invalidateQueries({ queryKey: ["pools"] });
      queryClient.invalidateQueries({ queryKey: ["replicas"] });
      onDeleted();
      onClose();
    },
    onError: (err) => {
      void alert({
        title: "Failed to delete service",
        tone: "danger",
        description:
          err instanceof Error ? err.message : "Failed to delete service.",
      });
    },
  });

  // Two distinct gates so the friction matches the blast radius:
  //   - draft   → type "confirmed" (low-stakes, but still not a single
  //               misclick away from blowing up the row)
  //   - active  → type the current display name (high-stakes; we use
  //               the rename-aware label so the modal reflects what the
  //               operator just typed in the rename field instead of
  //               the immutable slug they may have never seen).
  const DRAFT_CONFIRM_TOKEN = "confirmed";
  const expected = isDraft ? DRAFT_CONFIRM_TOKEN : displayName;
  const tokenMatches = typed.trim() === expected;
  const canSubmit = !remove.isPending && tokenMatches;
  // Surface the slug only when it actually differs from the label so we
  // don't print "svc-foo · svc-foo".
  const slugDiffersFromLabel = displayName !== service.name;

  return (
    <Modal
      open={open}
      onClose={remove.isPending ? () => {} : onClose}
      size="md"
      title={isDraft ? "Discard draft service?" : "Delete service?"}
      subtitle={
        isDraft ? (
          <>
            <strong>{displayName}</strong>
            {slugDiffersFromLabel && (
              <>
                {" "}
                <span className="muted">
                  (<code className="mono">{service.name}</code>)
                </span>
              </>
            )}{" "}
            · never rolled out
          </>
        ) : (
          <>
            <strong>{displayName}</strong>
            {slugDiffersFromLabel && (
              <>
                {" "}
                <span className="muted">
                  (<code className="mono">{service.name}</code>)
                </span>
              </>
            )}{" "}
            · {pools.length} pool{pools.length === 1 ? "" : "s"} ·{" "}
            {liveReplicaCount} live replica
            {liveReplicaCount === 1 ? "" : "s"}
          </>
        )
      }
      headerExtras={
        <Pill tone="danger">
          <AlertTriangle
            style={{
              width: 12,
              height: 12,
              verticalAlign: "middle",
              marginRight: 4,
            }}
          />
          irreversible
        </Pill>
      }
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={remove.isPending}
          >
            Cancel
          </button>
          <div className="right" />
          <button
            type="button"
            className="btn btn--danger"
            disabled={!canSubmit}
            onClick={() => remove.mutate()}
          >
            <Trash2 />
            {remove.isPending
              ? "deleting…"
              : isDraft
                ? "Discard draft"
                : "Delete service"}
          </button>
        </>
      }
    >
      {isDraft ? (
        <>
          <p style={{ marginTop: 0 }}>
            <strong>{displayName}</strong> hasn&rsquo;t been rolled out yet
            — there are no live replicas to tear down. The draft row,
            its workload spec, and any pools you&rsquo;ve attached will be
            removed.
          </p>
          {pools.length > 0 && (
            <p className="muted" style={{ fontSize: 12 }}>
              {pools.length} attached pool{pools.length === 1 ? "" : "s"}{" "}
              will be deleted along with the service.
            </p>
          )}

          <ConfirmInput
            label={
              <>
                Type{" "}
                <code className="mono" style={{ userSelect: "all" }}>
                  {expected}
                </code>{" "}
                to confirm
              </>
            }
            placeholder={expected}
            value={typed}
            onChange={setTyped}
            hint="A small typing step so the trash icon is never one misclick away from discarding your work-in-progress."
          />
        </>
      ) : (
        <>
          <p style={{ marginTop: 0 }}>
            Deleting <strong>{displayName}</strong> will remove every
            attached pool and tear down all running replicas. Routing
            rules, schedules, and rollout history for this service will be
            permanently lost.
          </p>

          <ImpactSummary
            pools={pools.length}
            liveReplicas={liveReplicaCount}
          />

          <ConfirmInput
            label={
              <>
                Type{" "}
                <code className="mono" style={{ userSelect: "all" }}>
                  {expected}
                </code>{" "}
                to confirm
              </>
            }
            placeholder={expected}
            value={typed}
            onChange={setTyped}
            hint={
              <>
                Case-sensitive. Type the current name exactly as shown.
                {slugDiffersFromLabel && (
                  <>
                    {" "}
                    The underlying slug{" "}
                    <code className="mono">{service.name}</code> stays
                    unchanged regardless of renames.
                  </>
                )}
              </>
            }
          />
        </>
      )}
    </Modal>
  );
}

function ConfirmInput({
  label,
  placeholder,
  value,
  onChange,
  hint,
}: {
  label: React.ReactNode;
  placeholder: string;
  value: string;
  onChange: (next: string) => void;
  hint: React.ReactNode;
}) {
  return (
    <div className="form-grid" style={{ marginTop: 16 }}>
      <label>{label}</label>
      <div className="field">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus
          spellCheck={false}
          autoComplete="off"
        />
        <div className="field-hint">{hint}</div>
      </div>
    </div>
  );
}

function ImpactSummary({
  pools,
  liveReplicas,
}: {
  pools: number;
  liveReplicas: number;
}) {
  return (
    <div className="kv" style={{ marginTop: 12 }}>
      <span className="k">pools</span>
      <span className="v mono">{pools}</span>
      <span className="k">live replicas</span>
      <span className="v mono">{liveReplicas}</span>
    </div>
  );
}
